"use client";

import { PageEigenePriceSchema } from "@/sanity/pages/PageEigenePriceTemplate";
import Image from "next/image";
import leaves from "@/public/global/svg/leaves.svg";
import {
  SliderLinesBg,
  SliderLinesBgMobile,
} from "../howItWorks/LinesBg";
import { useState, useEffect } from "react";
import { CTAButton } from "@/shadcn/ui/button";

export default function Table({ data }: { data: PageEigenePriceSchema }) {
  const [isMobile, setIsMobile] = useState(false);
  const [activeChipIndex, setActiveChipIndex] = useState(0);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!data?.tableSets || data.tableSets.length === 0) {
    return null;
  }

  const activeTableSet = data.tableSets[activeChipIndex];
  const tableColumns = activeTableSet?.columns || [];
  const rowLabels = activeTableSet?.rowLabels || [];

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Helper function to get value for a specific key from column data
  const getColumnValue = (column: typeof tableColumns[0], key: string) => {
    const dataItem = column.data?.find((item) => item.key === key);
    return dataItem?.value || '';
  };

  return (
    <section className="relative w-full pt-[7.5rem] pb-[10rem] lg:pb-0">
      <div className="container flex flex-col items-center justify-center relative z-10">
        {/* Stats Section */}
        {data?.tableStats && data.tableStats.length > 0 && (
          <div className="flex flex-col lg:flex-row items-center justify-between gap-[5rem] mb-[5rem] lg:mb-[8rem] w-[45rem] lg:min-w-[180.125rem] lg:px-[7.37rem] py-[4.62rem] bg-white border border-[#E5E5E5]">
            {data.tableStats.map((stat, index) => (
              <div key={index} className="flex items-center gap-[1.75rem]">
                <Image
                  src={leaves}
                  alt=""
                  className="w-[4.125rem] h-[6rem]"
                />
                <div className="flex flex-col text-center items-center justify-center">
                  <span className="text-[3.25rem] font-semibold text-[#103037] leading-none mb-[0.5rem]">
                    {stat.number}
                  </span>
                  <span className="text-[2rem] text-[#414141] leading-none">
                    {stat.label}
                  </span>
                </div>
                <Image
                  src={leaves}
                  alt=""
                  className="w-[4.125rem] h-[6rem] scale-x-[-1]"
                />
              </div>
            ))}
          </div>
        )}

        {/* Chips Section */}
        {data?.tableSets && data.tableSets.length > 0 && (
          <div className="flex flex-row items-center justify-center gap-[1rem] lg:gap-[4rem] mb-[-1rem]">
            {data.tableSets.map((tableSet, index) => (
              <button
                key={index}
                onClick={() => setActiveChipIndex(index)}
                className={`rounded-t-[3rem] lg:rounded-t-[5.25rem] text-center w-fit text-[1.5rem] lg:text-[2.25rem] tracking-[-0.0675rem] leading-none uppercase lg:py-[3.50rem] py-[2rem] lg:px-[7.78rem] px-[2.75rem] transition-all duration-200 cursor-pointer ${
                  activeChipIndex === index ? 'relative' : ''
                } ${index === 0 ? 'flex items-center gap-[1rem]' : ''}`}
                style={{
                  backgroundColor: activeChipIndex === index ? '#E6EDE9' : '#F7F9F8',
                  color: activeChipIndex === index ? '#000' : '#66666666',
                  fontWeight: activeChipIndex === index ? '700' : '500',
                }}
              >
                {tableSet.chipLabel}
                <>
                  <div
                    className="lg:flex hidden absolute bottom-0 left-0 w-[6rem] h-[6rem] transition-opacity duration-200"
                    style={{
                      backgroundColor: 'transparent',
                      borderBottomRightRadius: '6rem',
                      boxShadow: '2.625rem 2.625rem 0 0 #E6EDE9',
                      transform: 'translate(-90%, 10%)',
                      opacity: activeChipIndex === index ? 1 : 0,
                      pointerEvents: 'none',
                    }}
                  />
                  <div
                    className="lg:flex hidden absolute bottom-0 right-0 w-[6rem] h-[6rem] transition-opacity duration-200"
                    style={{
                      backgroundColor: 'transparent',
                      borderBottomLeftRadius: '6rem',
                      boxShadow: '-2.625rem 2.625rem 0 0 #E6EDE9',
                      transform: 'translate(90%, 10%)',
                      opacity: activeChipIndex === index ? 1 : 0,
                      pointerEvents: 'none',
                    }}
                  />
                </>
              </button>
            ))}
          </div>
        )}

        {/* Table Container */}
        <div
          className="relative w-full lg:w-fit lg:min-w-[150rem] bg-[#E6EDE9] rounded-[2rem] lg:rounded-[3rem] p-[1.5rem] lg:p-[3rem] pt-[3.5rem] lg:pt-[4rem] overflow-x-auto"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#10303788 #E6EDE9',
          } as React.CSSProperties}
        >
          <div className="relative grid gap-0" style={{
            gridTemplateColumns: isMobile
              ? tableColumns.length === 1
                ? '20rem 1fr'
                : `20rem repeat(${tableColumns.length}, 22rem)`
              : `25rem repeat(${tableColumns.length}, 1fr)`
          }}>
            {/* Most Popular Column Backgrounds */}
            {tableColumns.map((column, colIndex) => {
              if (!column.mostPopular) return null;

              return (
                <div
                  key={`bg-${colIndex}`}
                  className="absolute top-0 h-[calc(100%+2rem)] border border-[#57B480] rounded-[2.75rem] bg-[#57B480]/10 pointer-events-none"
                  style={{
                    left: isMobile
                      ? `calc(20rem + ${colIndex * 22}rem)`
                      : `calc(25rem + ${colIndex} * (100% - 25rem) / ${tableColumns.length})`,
                    width: isMobile
                      ? '22rem'
                      : `calc((100% - 25rem) / ${tableColumns.length})`,
                    zIndex: 0,
                  }}
                >
                  <span className="absolute lg:top-[-2rem] top-[-1.5rem] left-1/2 -translate-x-1/2 text-white bg-[#57B480] lg:px-[2rem] px-[1.5rem] lg:py-[1rem] pt-[.80rem] pb-[.75rem] rounded-[.75rem] text-[1.25rem] lg:text-[1.75rem] leading-none uppercase min-w-[13rem] lg:min-w-[17.5rem]">most popular</span>
                </div>
              );
            })}

            {/* Header Row */}
            <div className=""></div>
            {tableColumns.map((column, colIndex) => (
              <div
                key={colIndex}
                className="relative text-center text-[#103037] pt-[2.75rem] lg:pt-[4rem] pb-[2rem] lg:pb-[3rem] font-semibold text-[2.75rem]"
                style={{ zIndex: 1 }}
              >
                {formatPrice(column.price)}
              </div>
            ))}

            {/* Data Rows */}
            {rowLabels.map((row, rowIndex) => {
              const isLastRow = rowIndex === rowLabels.length - 1;

              return (
                <>
                  <div
                    key={`label-${rowIndex}`}
                    className={`relative ${
                      isLastRow
                        ? 'lg:flex hidden font-[500] text-[2rem] text-white/50 items-center pl-[2.75rem] lg:pl-[7.09rem] py-[2rem] bg-[#103037] rounded-l-[1.5rem]'
                        : `py-[3rem] pl-[2.75rem] lg:pl-[7.09rem] text-[2rem] text-[#103037]/50 font-[500] tracking-tight flex items-center ${
                            rowIndex % 2 === 0 ? 'bg-[#F6F9F7] rounded-l-[1.5rem]' : ''
                          }`
                    }`}
                    style={{ zIndex: 1 }}
                  >
                    {row.label}
                  </div>
                  {tableColumns.map((column, colIndex) => (
                    <div
                      key={`${rowIndex}-${colIndex}`}
                      className={`relative ${
                        isLastRow
                          ? 'lg:flex hidden py-[2rem] px-[1rem] items-center justify-center bg-[#103037] last:rounded-r-[1.5rem] last:mr-[1.5rem]'
                          : `py-[1.5rem] px-[2.5rem] lg:px-[7.25rem] text-center text-[2rem] text-[#103037]/80 leading-[120%] flex items-center justify-center ${
                              rowIndex % 2 === 0 ? 'bg-[#F6F9F7]' : ''
                            } ${
                              colIndex === tableColumns.length - 1
                                ? `rounded-r-[1.5rem] ${isMobile && tableColumns.length === 1 ? 'mr-0' : 'mr-[1.5rem]'}`
                                : ''
                            }`
                      }`}
                      style={{ zIndex: 1 }}
                    >
                      {isLastRow ? (
                        <button className="text-white font-[600] text-[2.75rem]">
                          {getColumnValue(column, row.key)}
                        </button>
                      ) : (
                        getColumnValue(column, row.key)
                      )}
                    </div>
                  ))}
                </>
              );
            })}
          </div>

          {activeTableSet?.subtitleTable && (
            <p className="text-[1.5rem] text-[#103037]/60 pl-[1rem] pt-[1.5rem] mb-0">
              {activeTableSet.subtitleTable}
            </p>
          )}
        </div>
        
        {data?.tableCTA && (
          <CTAButton className="w-full lg:w-fit mt-[3rem] lg:mt-[5.5rem] mb-[5rem] lg:mb-0" data={data?.tableCTA} />
        )}
      </div>

      <SliderLinesBg hideBox className="hidden absolute inset-0 h-full lg:block mx-auto pointer-events-none" style={{ zIndex: 0 }} />
      <SliderLinesBgMobile hideBox className="block absolute inset-0 w-full h-full lg:hidden pointer-events-none" style={{ zIndex: 0 }} />
    </section>
  );
}
